import React, { useMemo, useState } from 'react';
import { track, useTrackScreen } from '@/lib/analytics';
import { View, Text, ScrollView, Pressable } from 'react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { ScreenFrame } from '@/components/ScreenFrame';
import { useRouter } from 'expo-router';
import {
  TrendingUp,
  TrendingDown,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Briefcase,
  LayoutGrid,
  FileSearch,
  MessageCircle,
  Sparkles,
  Clock,
  AlertCircle,
  Target,
  Eye,
  EyeOff,
  Star,
  Building2,
  Zap,
  Newspaper,
  Wallet,
  Inbox,
  XCircle,
} from 'lucide-react-native';
import Animated, {
  FadeInDown,
  FadeInUp,
  FadeInRight,
  } from 'react-native-reanimated';
import { labelColor, scoreToLabel, capitalizeLabel } from '@/lib/scoring/score-display';
import { useThemeColors } from '@/lib/useThemeColors';
import { useProfileStore } from '@/lib/state/profile-store';
import { useDealflowStore } from '@/lib/state/dealflow-store';
import { useAuth } from '@/lib/supabase/auth';
import { useInvestorPortfolioEntries } from '@/lib/supabase/investor-queries';
import { useConversations, mapConversationRow, useInvestorProfile } from '@/lib/supabase';
import { useMyDataRoomAccessRequests } from '@/lib/supabase/api';
import { useSubmissions } from '@/lib/supabase/submissions';
import { selectDDReviews, countPendingDDReviews } from '@/lib/dd/dd-entry';
import { appearsOn, awaitingTriage } from '@/lib/dealflow/stage';
import { buildPendingActions } from '@/lib/investor/pending-actions';
import { formatCurrency } from '@/lib/utils/format';
import { usePhoneShell } from '@/lib/useShell';
import { useRealtimeNotifications } from '@/lib/supabase/realtime';
import { mode } from '@/lib/config/app-mode';
import { hapticLight } from '@/lib/haptics';
import { AppLayout } from '@/components/AppLayout';
import { OnboardingIncompleteBanner } from '@/components/onboarding/OnboardingIncompleteBanner';
import AILauncherFAB from '@/components/AILauncherFAB';
import { SectionDivider } from '@/components/SectionDivider';
import { ModeBadge } from '@/components/ModeIndicator';
import { PlatformDealflowLocked } from '@/components/investor/PlatformDealflowLocked';
import { useCurrentTier } from '@/lib/state/subscription-store';
import { investorCanSeePlatformDealflow } from '@/lib/subscription/dealflow-gate';
import type { InvestorTier } from '@/lib/subscription/types';

// Mock data for dashboard - in production, these would come from the store/API
const mockInvestorStats = {
  totalInvested: 2400000,
  totalReturns: 18.2, // percentage
  portfolioValue: 4250000,
  portfolioValueChange: 12.5, // percentage this month
};

// Sample-mode figures. Keyed to the four TILES, not to the seven spine stages
// — `diligence` is the whole DD surface (awaiting_access + diligence), same as
// the real `pipelineCounts` below.
const mockPipelineStats = {
  screening: 14,
  interested: 3,
  diligence: 5,
  passed: 8,
};

// Helper to format relative time
const getRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 14) return '1 week ago';
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 60) return '1 month ago';
  return `${Math.floor(diffDays / 30)} months ago`;
};

export default function InvestorDashboard() {
  useTrackScreen('InvestorDashboard');
  const router = useRouter();
  const colors = useThemeColors();
  const phoneShell = usePhoneShell();
  // D3 entitlement gate: the platform Deal Pipeline is a paid feature. Free
  // investors see a teaser instead. Optimistic during subscription load (P0-4).
  const investorTier = useCurrentTier() as InvestorTier;
  const canSeePlatformDealflow = investorCanSeePlatformDealflow(investorTier);
  const investorProfile = useProfileStore((s) => s.investorProfile);
  const user = useProfileStore((s) => s.user);
  // Server-fetched investor_profiles row. Named apart from the store's
  // `investorProfile` (below) because access requests must key off the real
  // investor_profiles.id, not the auth uid or a stale persisted copy.
  const { data: investorProfileRow } = useInvestorProfile(
    mode.showSampleData ? undefined : user?.id,
  );
  const dealflow = useDealflowStore((s) => s.dealflow);
  // Portfolio comes from the DATABASE, via the same hook the Portfolio screen
  // uses — not from the local dealflow store.
  //
  // The store's `portfolio` is only ever written by `setPortfolio(getMockPortfolio())`
  // at demo login (login.tsx, LaunchLandingPage.tsx). Nothing populates it in
  // production, so this dashboard reported an empty portfolio no matter what the
  // investor had actually saved. Two screens reading two different sources is
  // how they silently disagreed.
  // Named `authUser` deliberately — there is already a `user` from the profile
  // store above, and this must be the AUTH user id (auth.uid()), which is what
  // portfolio_companies is keyed by.
  const { user: authUser } = useAuth();
  // One source for both modes: the hook returns mock data in sample mode and
  // the database otherwise, so there is no second slice to fall out of step.
  //
  // `totalCompanies` counts what the Portfolio SCREEN lists — holdings plus
  // declared track record. The tile used to count holdings alone, so an
  // investor with declared past investments and no paid holdings read
  // "0 active companies" here and a full list one tap away. The money metrics
  // below stay over holdings: track-record entries carry no cheque size, so
  // including them would report an invested total nobody invested.
  const { holdings: portfolio, totalCompanies } = useInvestorPortfolioEntries(authUser?.id);
  // Unread badge for the mobile bell below.
  const { unreadCount } = useRealtimeNotifications(authUser?.id);

  // Submissions still awaiting a decision — what the header Inbox badge counts.
  // Deliberately not "all submissions": a queue badge should shrink as you work
  // it, so anything already triaged (shortlisted / archived / rejected /
  // claimed) drops out.
  // AUTH user id — submissions.investor_id references auth.users(id).
  // This passed `investorProfile?.id` when the header Inbox badge was added
  // earlier today, so the count would have been 0 for every investor in
  // production. Caught by the id brands, not by a test or a review.
  const { data: submissions = [] } = useSubmissions(authUser?.id);
  const awaitingTriageCount = submissions.filter((s) =>
    ['pending', 'scoring', 'scored', 'unscorable'].includes(s.status)
  ).length;
  const commitments = useDealflowStore((s) => s.commitments);

  // Unread messages — sum of investor-side unread from real conversations.
  const { data: conversationRows } = useConversations(investorProfile?.id, 'investor');

  // Commitment stats
  const myCommitments = useMemo(() => {
    return commitments.filter(
      (c) => c.investorId === investorProfile?.id && (c.status === 'active' || c.status === 'converted')
    );
  }, [commitments, investorProfile?.id]);

  const totalCommitted = useMemo(() => myCommitments.reduce((sum, c) => sum + c.amount, 0), [myCommitments]);
  const softCount = useMemo(() => myCommitments.filter((c) => c.type === 'soft' && c.status === 'active').length, [myCommitments]);
  const hardCount = useMemo(() => myCommitments.filter((c) => c.type === 'hard' || c.status === 'converted').length, [myCommitments]);

  // Privacy mask — financial values hidden by default
  const [isMasked, setIsMasked] = useState(true);
  const mv = (value: string) => isMasked ? '••••••' : value;

  // Collapsible states — collapsed by default, user expands on demand
  const [pendingActionsExpanded, setPendingActionsExpanded] = useState(false);
  const [portfolioNewsExpanded, setPortfolioNewsExpanded] = useState(false);

  // Calculate portfolio metrics
  const portfolioMetrics = useMemo(() => {
    if (portfolio.length === 0) {
      // In mock mode, show sample stats; in production, show zeros
      if (mode.showSampleData) {
        return {
          totalInvested: mockInvestorStats.totalInvested,
          portfolioValue: mockInvestorStats.portfolioValue,
          totalReturns: mockInvestorStats.totalReturns,
          companiesCount: 3,
        };
      }
      return { totalInvested: 0, portfolioValue: 0, totalReturns: 0, companiesCount: totalCompanies };
    }

    const totalInvested = portfolio.reduce((sum, p) => sum + p.investmentAmount, 0);
    const portfolioValue = portfolio.reduce((sum, p) => {
      const currentValue = (p.equity / 100) * p.currentValuation;
      return sum + currentValue;
    }, 0);
    const totalReturns = totalInvested > 0 ? ((portfolioValue - totalInvested) / totalInvested) * 100 : 0;

    return {
      totalInvested,
      portfolioValue,
      totalReturns,
      companiesCount: totalCompanies,
    };
  }, [portfolio, totalCompanies]);

  // Pipeline counts from dealflow. 'screening' absorbs the retired
  // 'new' + 'reviewing' pair, so it is counted once, not twice under two labels.
  //
  // FOUR TILES, SEVEN STAGES — deliberately not one field per stage. When
  // `awaiting_access` joined the spine (20260805140000) the choice was a fifth
  // tile or folding it in; five `flex-1` tiles in one row are unreadable on a
  // phone, and "awaiting the founder" is not a separate thing an investor acts
  // on — it is the front half of doing diligence.
  //
  // So `diligence` counts the whole DD SURFACE via `appearsOn`, exactly as
  // `selectDDReviews` and the dealflow chip do. The tile therefore agrees with
  // the Due Diligence tab it links to, and no live deal falls into a gap
  // between tiles. Add a stage to the spine and decide HERE which tile it
  // belongs to — a new stage counted nowhere is silent, not loud.
  const pipelineCounts = useMemo(() => {
    if (dealflow.length === 0) {
      return mode.showSampleData
        ? mockPipelineStats
        : { screening: 0, interested: 0, diligence: 0, passed: 0 };
    }

    return {
      screening: dealflow.filter((d) => d.status === 'screening').length,
      interested: dealflow.filter((d) => d.status === 'interested').length,
      diligence: dealflow.filter((d) => appearsOn(d.status, 'diligence')).length,
      passed: dealflow.filter((d) => d.status === 'rejected').length,
    };
  }, [dealflow]);

  // Get top recommended deals (highest scores still awaiting triage)
  const topDeals = useMemo(() => {
    const newDeals = dealflow.filter((d) => awaitingTriage(d.status));
    // Unscored deals sort last — absence is not a low score, but it must not
    // outrank a real one either.
    return newDeals
      .sort((a, b) => (b.readinessScore ?? -1) - (a.readinessScore ?? -1))
      .slice(0, 2);
  }, [dealflow]);

  // Unread messages count
  const unreadMessagesCount = useMemo(
    () =>
      (conversationRows ?? [])
        .map((row) => mapConversationRow(row))
        .reduce((sum, c) => sum + c.unreadCount, 0),
    [conversationRows]
  );

  // Pending due diligence count.
  //
  // Derived from the SAME selector the DD screen renders (`selectDDReviews`).
  // It used to be `status in (reviewing, interested)` while the DD screen
  // filtered on the since-retired `dataRoomAccess` — two disjoint predicates, so this card
  // advertised reviews that the DD screen could never show. Keep them on one
  // source or the drift comes straight back.
  const { data: myAccessRequests = [] } = useMyDataRoomAccessRequests(
    mode.showSampleData ? undefined : investorProfileRow?.id,
  );
  const pendingDueDiligence = useMemo(
    () => countPendingDDReviews(selectDDReviews(dealflow, myAccessRequests)),
    [dealflow, myAccessRequests],
  );

  // What is genuinely waiting on the investor. One array that is both counted
  // and mapped below, so the header count and the rendered rows cannot desync
  // — see src/lib/investor/pending-actions.ts for why this exists.
  const pendingActions = useMemo(
    () =>
      buildPendingActions({
        pendingDueDiligence,
        unreadMessages: unreadMessagesCount,
      }),
    [pendingDueDiligence, unreadMessagesCount],
  );
  const totalPendingActions = pendingActions.length;

  // Derive portfolio updates from real portfolio data
  const portfolioUpdates = useMemo(() => {
    const updates: Array<{
      id: string;
      startupId: string;
      companyName: string;
      type: 'milestone' | 'traction' | 'funding' | 'team' | 'product' | 'announcement';
      title: string;
      date: string;
    }> = [];

    portfolio.forEach((company) => {
      if (company.updates) {
        company.updates.forEach((update) => {
          updates.push({
            id: update.id,
            startupId: company.startup.id,
            companyName: company.startup.company.name,
            type: update.type,
            title: update.title,
            date: update.date,
          });
        });
      }
    });

    // Sort by date descending and take the most recent ones
    return updates
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 5);
  }, [portfolio]);

  // Get greeting based on time
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const userName = user?.name?.split(' ')[0] || 'Investor';

  return (
    <AppLayout title="Dashboard" showSearch>
      <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
        {/* PINNED. The controls used to sit inside the ScrollView below, so
           scrolling the dashboard took the only route to the account off screen
           — and no other primary page offered one at all. Inbox comes with them:
           it keeps its dashboard placement and its badge, and gains persistence.
           The greeting stays in the body, where descriptive hero content belongs.
           No bottomEdge: tab screen. */}
        <ScreenFrame
          actions={
            <>
                  {/*
                Inbox — MOBILE ONLY, and deliberately beside the bell rather
                than in the tab bar. Both are inboxes of unread things that
                arrive on their own, so they belong at the same level; the
                tab bar is capped at six and Portfolio took that slot,
                being somewhere you browse rather than somewhere a badge
                sends you.

                It is deliberately kept SEPARATE from the two global
                utilities that follow it: Inbox is investor-specific triage,
                Notifications and the account avatar are the same two
                controls in the same order in both personas.
              */}
              {phoneShell && (
                <AnimatedPressable
                  onPress={() => router.push('/(investor)/submissions')}
                  accessibilityRole="button"
                  accessibilityLabel={
                    awaitingTriageCount > 0
                      ? `Inbox, ${awaitingTriageCount} awaiting review`
                      : 'Inbox'
                  }
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 22,
                    backgroundColor: colors.surface.primary,
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Inbox size={20} color={colors.text.secondary} />
                  {awaitingTriageCount > 0 && (
                    <View
                      style={{
                        position: 'absolute',
                        top: 6,
                        right: 6,
                        minWidth: 16,
                        height: 16,
                        borderRadius: 8,
                        paddingHorizontal: 4,
                        backgroundColor: colors.primary[600],
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      {/* A count, not a dot: an investor triaging a queue
                          needs to know whether it is 1 or 20. */}
                      <Text
                        style={{ color: 'white', fontSize: 10, fontWeight: '700' }}
                        numberOfLines={1}
                      >
                        {awaitingTriageCount > 99 ? '99+' : awaitingTriageCount}
                      </Text>
                    </View>
                  )}
                </AnimatedPressable>
              )}
              {/*
                The two GLOBAL utilities — Notifications then the account
                avatar — now come from one shared component so both personas
                get the same controls in the same order and the same place.
                The bell's hand-rolled twin used to live here; the avatar is
                new and replaces the Profile tab.
              */}
              <ScreenFrame.GlobalActions />
            </>
          }
        >
          <ScrollView
            className="flex-1"
            showsVerticalScrollIndicator={false}
            contentContainerStyle={{ paddingBottom: 100 }}
          >
            <OnboardingIncompleteBanner />
            {/* Header / Greeting */}
            <Animated.View
              entering={FadeInDown.delay(50).duration(500)}
              className="px-5 pt-6 pb-2"
            >
              <View className="flex-row items-center gap-2">
                <View className="flex-1">
                  <Text style={{ color: colors.text.tertiary }} className="text-base" numberOfLines={1}>
                    {getGreeting()},
                  </Text>
                  <Text style={{ color: colors.text.primary }} className="text-2xl font-bold mt-1" numberOfLines={1}>
                    {userName}
                  </Text>
                </View>
                <View className="shrink-0">
                  <ModeBadge />
                </View>
              </View>
              {investorProfile?.firm && (
                <Text style={{ color: colors.text.secondary }} className="text-sm mt-1">
                  {investorProfile.firm}
                </Text>
              )}
            </Animated.View>

            {/* Portfolio Overview Hero Card */}
            <Animated.View
              entering={FadeInUp.delay(100).duration(600)}
              className="mx-5 mt-4"
            >
              <AnimatedPressable
                onPress={() => router.push('/(investor)/portfolio')}
                className="rounded-3xl p-5"
                style={{
                  backgroundColor: colors.surface.primary,
                  borderWidth: 1,
                  borderColor: colors.border.light,
                  shadowColor: '#000',
                  shadowOpacity: 0.08,
                  shadowRadius: 16,
                  shadowOffset: { width: 0, height: 4 },
                  elevation: 4,
                }}
              >
                <View className="flex-row items-center justify-between mb-4">
                  <View className="flex-row items-center gap-2">
                    <Briefcase size={18} color={colors.primary[600]} />
                    <Text
                      style={{ color: colors.text.tertiary }}
                      className="text-xs uppercase tracking-wider font-medium"
                    >
                      Portfolio Overview
                    </Text>
                  </View>
                  <View className="flex-row items-center gap-2">
                    <Pressable
                      onPress={(e) => { e.stopPropagation(); hapticLight(); setIsMasked(!isMasked); }}
                      accessibilityRole="switch"
                      accessibilityLabel={isMasked ? 'Show financial values' : 'Hide financial values'}
                      accessibilityState={{ checked: !isMasked }}
                      className="flex-row items-center gap-1 px-2 py-1 rounded-full active:opacity-70"
                      style={{
                        backgroundColor: colors.surface.secondary,
                        borderWidth: 1,
                        borderColor: colors.border.light,
                      }}
                      hitSlop={8}
                    >
                      {isMasked ? (
                        <EyeOff size={12} color={colors.text.tertiary} />
                      ) : (
                        <Eye size={12} color={colors.primary[600]} />
                      )}
                      <Text
                        style={{ color: isMasked ? colors.text.tertiary : colors.primary[600] }}
                        className="text-xs font-medium"
                      >
                        {isMasked ? 'Show' : 'Hide'}
                      </Text>
                    </Pressable>
                    {!isMasked && (
                      <View
                        className="flex-row items-center gap-1 px-2 py-1 rounded-full"
                        style={{
                          backgroundColor:
                            portfolioMetrics.totalReturns >= 0 ? colors.traffic.greenLight : colors.red.light,
                        }}
                      >
                        {portfolioMetrics.totalReturns >= 0 ? (
                          <TrendingUp size={12} color={colors.success} />
                        ) : (
                          <TrendingDown size={12} color={colors.error} />
                        )}
                        <Text
                          className="text-xs font-semibold"
                          style={{
                            color: portfolioMetrics.totalReturns >= 0 ? colors.success : colors.error,
                          }}
                        >
                          {portfolioMetrics.totalReturns >= 0 ? '+' : ''}
                          {portfolioMetrics.totalReturns.toFixed(1)}%
                        </Text>
                      </View>
                    )}
                  </View>
                </View>

                <View className="flex-row items-end justify-between">
                  <View>
                    <Text style={{ color: colors.text.tertiary }} className="text-sm mb-1">
                      Portfolio Value
                    </Text>
                    <Text style={{ color: colors.text.primary }} className="text-4xl font-bold">
                      {mv(formatCurrency(portfolioMetrics.portfolioValue))}
                    </Text>
                  </View>

                  <View className="items-end">
                    <Text style={{ color: colors.text.tertiary }} className="text-xs mb-1">
                      Total Invested
                    </Text>
                    <Text style={{ color: colors.text.secondary }} className="text-lg font-semibold">
                      {mv(formatCurrency(portfolioMetrics.totalInvested))}
                    </Text>
                  </View>
                </View>

                {/* Companies Count */}
                <View
                  className="flex-row items-center justify-between mt-4 pt-4 border-t"
                  style={{ borderColor: colors.border.light }}
                >
                  <View className="flex-row items-center gap-2">
                    <Building2 size={16} color={colors.text.tertiary} />
                    <Text style={{ color: colors.text.secondary }} className="text-sm">
                      {portfolioMetrics.companiesCount} active companies
                    </Text>
                  </View>
                  <View className="flex-row items-center gap-1">
                    <Text style={{ color: colors.primary[600] }} className="text-sm font-medium">
                      View Details
                    </Text>
                    <ChevronRight size={16} color={colors.primary[600]} />
                  </View>
                </View>
              </AnimatedPressable>
            </Animated.View>

            {/* Section: Action Items */}
            <View className="px-5">
              <SectionDivider label="Action Items" icon={AlertCircle} marginTop={24} marginBottom={12} />
            </View>

            {/* Pending Actions - Collapsible with reddish accent */}
            <Animated.View
              entering={FadeInUp.delay(150).duration(500)}
              className="px-5"
            >
              <View
                className="rounded-2xl overflow-hidden"
                style={{
                  backgroundColor: colors.surface.primary,
                  borderWidth: 1,
                  borderColor: totalPendingActions > 0 ? colors.red.light : colors.border.light,
                  shadowColor: '#000',
                  shadowOpacity: 0.05,
                  shadowRadius: 10,
                  shadowOffset: { width: 0, height: 2 },
                  elevation: 2,
                }}
              >
                {/* Header - Collapsible */}
                <Pressable
                  onPress={() => setPendingActionsExpanded(!pendingActionsExpanded)}
                  className="flex-row items-center justify-between p-4"
                  style={{
                    backgroundColor: totalPendingActions > 0 ? colors.red.light : colors.surface.primary,
                  }}
                >
                  <View className="flex-row items-center gap-3">
                    <View
                      className="w-8 h-8 rounded-lg items-center justify-center"
                      style={{ backgroundColor: totalPendingActions > 0 ? colors.red.main : colors.light[300] }}
                    >
                      <AlertCircle size={16} color={totalPendingActions > 0 ? 'white' : colors.text.tertiary} />
                    </View>
                    <View>
                      <Text style={{ color: colors.text.primary }} className="font-semibold">
                        Pending Actions
                      </Text>
                      <Text style={{ color: colors.text.secondary }} className="text-xs">
                        {totalPendingActions > 0 ? `${totalPendingActions} items need attention` : 'All caught up!'}
                      </Text>
                    </View>
                  </View>
                  {pendingActionsExpanded ? (
                    <ChevronUp size={20} color={colors.text.tertiary} />
                  ) : (
                    <ChevronDown size={20} color={colors.text.tertiary} />
                  )}
                </Pressable>

                {/* Content - Collapsible. Mapped from `pendingActions` — the
                    SAME array whose length drove the header count above —
                    so a row can never appear (or vanish) without the count
                    following it. */}
                {pendingActionsExpanded && (
                  <View style={{ borderTopWidth: 1, borderTopColor: colors.border.light }}>
                    {pendingActions.map((action, index) => {
                      const borderStyle = {
                        borderBottomWidth: index < pendingActions.length - 1 ? 1 : 0,
                        borderBottomColor: colors.border.light,
                      };

                      if (action.kind === 'due-diligence') {
                        return (
                          <AnimatedPressable
                            key="due-diligence"
                            onPress={() => router.push('/(investor)/duediligence')}
                            className="flex-row items-center p-4"
                            style={borderStyle}
                          >
                            <View
                              className="w-10 h-10 rounded-xl items-center justify-center mr-3"
                              style={{ backgroundColor: colors.amber.light }}
                            >
                              <FileSearch size={20} color={colors.warning} />
                            </View>
                            <View className="flex-1">
                              <Text style={{ color: colors.text.primary }} className="font-medium" numberOfLines={1}>
                                {action.count} due diligence review{action.count > 1 ? 's' : ''} pending
                              </Text>
                              <Text style={{ color: colors.text.tertiary }} className="text-sm" numberOfLines={1}>
                                Data rooms awaiting your review
                              </Text>
                            </View>
                            <ChevronRight size={20} color={colors.light[500]} />
                          </AnimatedPressable>
                        );
                      }

                      return (
                        <AnimatedPressable
                          key="messages"
                          onPress={() => router.push('/(investor)/investor-messages')}
                          className="flex-row items-center p-4"
                          style={borderStyle}
                        >
                          <View
                            className="w-10 h-10 rounded-xl items-center justify-center mr-3"
                            style={{ backgroundColor: colors.primary[50] }}
                          >
                            <MessageCircle size={20} color={colors.primary[600]} />
                          </View>
                          <View className="flex-1">
                            <Text style={{ color: colors.text.primary }} className="font-medium" numberOfLines={1}>
                              {action.count} unread message{action.count > 1 ? 's' : ''}
                            </Text>
                            <Text style={{ color: colors.text.tertiary }} className="text-sm" numberOfLines={1}>
                              From startups in your pipeline
                            </Text>
                          </View>
                          <ChevronRight size={20} color={colors.light[500]} />
                        </AnimatedPressable>
                      );
                    })}
                  </View>
                )}
              </View>
            </Animated.View>

            {/* Explore new opportunities — discovery, not a pending action.
                Rendered only when there is something to discover; otherwise
                it would advertise "0 new startups to review". */}
            {pipelineCounts.screening > 0 && (
              <Animated.View entering={FadeInUp.delay(200).duration(500)} className="px-5 mt-4">
                <AnimatedPressable
                  onPress={() => router.push('/(investor)/dealflow')}
                  className="flex-row items-center p-4 rounded-2xl"
                  style={{ backgroundColor: colors.surface.primary }}
                >
                  <View
                    className="w-10 h-10 rounded-xl items-center justify-center mr-3"
                    style={{ backgroundColor: colors.traffic.greenLight }}
                  >
                    <LayoutGrid size={20} color={colors.success} />
                  </View>
                  <View className="flex-1">
                    <Text style={{ color: colors.text.primary }} className="font-medium" numberOfLines={1}>
                      Explore new opportunities
                    </Text>
                    <Text style={{ color: colors.text.tertiary }} className="text-sm" numberOfLines={1}>
                      {pipelineCounts.screening} to review
                    </Text>
                  </View>
                  <ChevronRight size={20} color={colors.light[500]} />
                </AnimatedPressable>
              </Animated.View>
            )}

            {/* Active Commitments Card */}
            {myCommitments.length > 0 && (
              <Animated.View
                entering={FadeInUp.delay(175).duration(500)}
                className="px-5 mt-4"
              >
                <View
                  className="p-4 rounded-2xl"
                  style={{
                    backgroundColor: `${colors.primary[500]}10`,
                    borderWidth: 1,
                    borderColor: `${colors.primary[500]}20`,
                  }}
                >
                  <View className="flex-row items-center gap-2 mb-2">
                    <Wallet size={16} color={colors.primary[500]} />
                    <Text className="text-sm font-semibold" style={{ color: colors.text.primary }}>
                      Active Commitments
                    </Text>
                  </View>
                  <Text className="text-2xl font-bold" style={{ color: colors.primary[500] }}>
                    {mv(formatCurrency(totalCommitted))}
                  </Text>
                  <Text className="text-xs mt-1" style={{ color: colors.text.tertiary }}>
                    {hardCount} hard / {softCount} soft across {myCommitments.length} deal{myCommitments.length !== 1 ? 's' : ''}
                  </Text>
                </View>
              </Animated.View>
            )}

            {/* Portfolio News - Collapsible */}
            <Animated.View
              entering={FadeInUp.delay(200).duration(500)}
              className="px-5 mt-4"
            >
              <View
                className="rounded-2xl overflow-hidden"
                style={{
                  backgroundColor: colors.surface.primary,
                  borderWidth: 1,
                  borderColor: colors.border.light,
                  shadowColor: '#000',
                  shadowOpacity: 0.05,
                  shadowRadius: 10,
                  shadowOffset: { width: 0, height: 2 },
                  elevation: 2,
                }}
              >
                {/* Header - Collapsible */}
                <Pressable
                  onPress={() => setPortfolioNewsExpanded(!portfolioNewsExpanded)}
                  className="flex-row items-center justify-between p-4"
                >
                  <View className="flex-row items-center gap-3">
                    <View
                      className="w-8 h-8 rounded-lg items-center justify-center"
                      style={{ backgroundColor: colors.primary[50] }}
                    >
                      <Newspaper size={16} color={colors.primary[600]} />
                    </View>
                    <View>
                      <Text style={{ color: colors.text.primary }} className="font-semibold">
                        Portfolio News
                      </Text>
                      <Text style={{ color: colors.text.secondary }} className="text-xs">
                        {portfolioUpdates.length} recent updates
                      </Text>
                    </View>
                  </View>
                  <View className="flex-row items-center gap-2">
                    <AnimatedPressable onPress={() => router.push('/(investor)/portfolio')}>
                      <Text style={{ color: colors.primary[600] }} className="text-sm font-medium">
                        View All
                      </Text>
                    </AnimatedPressable>
                    {portfolioNewsExpanded ? (
                      <ChevronUp size={20} color={colors.text.tertiary} />
                    ) : (
                      <ChevronDown size={20} color={colors.text.tertiary} />
                    )}
                  </View>
                </Pressable>

                {/* Content - Collapsible */}
                {portfolioNewsExpanded && (
                  <View style={{ borderTopWidth: 1, borderTopColor: colors.border.light }}>
                    {portfolioUpdates.length === 0 ? (
                      <View className="p-4 items-center">
                        <Text style={{ color: colors.text.tertiary }} className="text-sm">
                          No recent updates from portfolio companies
                        </Text>
                      </View>
                    ) : (
                      portfolioUpdates.map((update, index) => (
                        <AnimatedPressable
                          key={update.id}
                          onPress={() => router.push(`/startup-detail?id=${update.startupId}`)}
                          className="flex-row items-center p-4"
                          style={{
                            borderBottomWidth: index < portfolioUpdates.length - 1 ? 1 : 0,
                            borderBottomColor: colors.border.light,
                          }}
                        >
                          {/* Update Icon */}
                          <View
                            className="w-10 h-10 rounded-full items-center justify-center mr-3"
                            style={{
                              backgroundColor:
                                update.type === 'funding' || update.type === 'milestone'
                                  ? colors.traffic.greenLight
                                  : update.type === 'traction'
                                    ? colors.amber.light
                                    : colors.primary[50],
                            }}
                          >
                            {update.type === 'funding' || update.type === 'milestone' ? (
                              <Star size={18} color={colors.success} />
                            ) : update.type === 'traction' ? (
                              <TrendingUp size={18} color={colors.warning} />
                            ) : (
                              <Zap size={18} color={colors.primary[600]} />
                            )}
                          </View>

                          <View className="flex-1">
                            <Text style={{ color: colors.text.primary }} className="font-medium">
                              {update.companyName}
                            </Text>
                            <Text style={{ color: colors.text.secondary }} className="text-sm" numberOfLines={1}>
                              {update.title}
                            </Text>
                          </View>

                          <View className="flex-row items-center gap-2">
                            <View className="flex-row items-center gap-1">
                              <Clock size={12} color={colors.text.tertiary} />
                              <Text style={{ color: colors.text.tertiary }} className="text-xs">
                                {getRelativeTime(update.date)}
                              </Text>
                            </View>
                            <ChevronRight size={16} color={colors.light[400]} />
                          </View>
                        </AnimatedPressable>
                      ))
                    )}
                  </View>
                )}
              </View>
            </Animated.View>

            {canSeePlatformDealflow ? (
            <>
            {/* Section: Deal Pipeline */}
            <View className="px-5">
              <SectionDivider label="Deal Pipeline" icon={Target} marginTop={24} marginBottom={12} />
            </View>

            {/* Pipeline Stats Row */}
            <Animated.View
              entering={FadeInUp.delay(250).duration(500)}
              className="px-5"
            >
              <View className="flex-row items-center justify-between mb-3">
                <Text style={{ color: colors.text.primary }} className="text-lg font-semibold">
                  Pipeline Overview
                </Text>
                <AnimatedPressable onPress={() => router.push('/(investor)/dealflow')}>
                  <Text style={{ color: colors.primary[600] }} className="text-sm font-medium">
                    View All
                  </Text>
                </AnimatedPressable>
              </View>

              {/* FOUR TILES, SEVEN STAGES — deliberately not one tile per
                  stage (see the full rationale at :208-218 above). Each of
                  the four is backed by a distinct filter in `pipelineCounts`
                  so no two tiles can ever show the same number under
                  different labels. */}
              <View className="flex-row gap-3">
                {/* Screening */}
                <AnimatedPressable
                  onPress={() => router.push('/(investor)/dealflow')}
                  className="flex-1 rounded-2xl p-4"
                  style={{
                    backgroundColor: colors.surface.primary,
                    borderWidth: 1,
                    borderColor: colors.border.light,
                    shadowColor: '#000',
                    shadowOpacity: 0.05,
                    shadowRadius: 10,
                    shadowOffset: { width: 0, height: 2 },
                    elevation: 2,
                  }}
                >
                  <View
                    className="w-8 h-8 rounded-lg items-center justify-center mb-2"
                    style={{ backgroundColor: colors.primary[50] }}
                  >
                    <Zap size={16} color={colors.primary[600]} />
                  </View>
                  <Text style={{ color: colors.text.primary }} className="text-2xl font-bold" numberOfLines={1}>
                    {pipelineCounts.screening}
                  </Text>
                  <Text style={{ color: colors.text.tertiary }} className="text-xs" numberOfLines={1}>
                    Screening
                  </Text>
                </AnimatedPressable>

                {/* Interested */}
                <AnimatedPressable
                  onPress={() => router.push('/(investor)/dealflow')}
                  className="flex-1 rounded-2xl p-4"
                  style={{
                    backgroundColor: colors.surface.primary,
                    borderWidth: 1,
                    borderColor: colors.border.light,
                    shadowColor: '#000',
                    shadowOpacity: 0.05,
                    shadowRadius: 10,
                    shadowOffset: { width: 0, height: 2 },
                    elevation: 2,
                  }}
                >
                  <View
                    className="w-8 h-8 rounded-lg items-center justify-center mb-2"
                    style={{ backgroundColor: colors.traffic.greenLight }}
                  >
                    <Target size={16} color={colors.success} />
                  </View>
                  <Text style={{ color: colors.text.primary }} className="text-2xl font-bold" numberOfLines={1}>
                    {pipelineCounts.interested}
                  </Text>
                  <Text style={{ color: colors.text.tertiary }} className="text-xs" numberOfLines={1}>
                    Interested
                  </Text>
                </AnimatedPressable>

                {/* Diligence */}
                <AnimatedPressable
                  onPress={() => router.push('/(investor)/duediligence')}
                  className="flex-1 rounded-2xl p-4"
                  style={{
                    backgroundColor: colors.surface.primary,
                    borderWidth: 1,
                    borderColor: colors.border.light,
                    shadowColor: '#000',
                    shadowOpacity: 0.05,
                    shadowRadius: 10,
                    shadowOffset: { width: 0, height: 2 },
                    elevation: 2,
                  }}
                >
                  <View
                    className="w-8 h-8 rounded-lg items-center justify-center mb-2"
                    style={{ backgroundColor: colors.amber.light }}
                  >
                    <FileSearch size={16} color={colors.warning} />
                  </View>
                  <Text style={{ color: colors.text.primary }} className="text-2xl font-bold" numberOfLines={1}>
                    {pipelineCounts.diligence}
                  </Text>
                  <Text style={{ color: colors.text.tertiary }} className="text-xs" numberOfLines={1}>
                    Diligence
                  </Text>
                </AnimatedPressable>

                {/* Passed — the UI label stays "Passed"; the data vocabulary
                    (DealStage) calls this stage 'rejected'. Deliberately
                    separate: see src/lib/dealflow/stage.ts. */}
                <AnimatedPressable
                  onPress={() => router.push('/(investor)/dealflow')}
                  className="flex-1 rounded-2xl p-4"
                  style={{
                    backgroundColor: colors.surface.primary,
                    borderWidth: 1,
                    borderColor: colors.border.light,
                    shadowColor: '#000',
                    shadowOpacity: 0.05,
                    shadowRadius: 10,
                    shadowOffset: { width: 0, height: 2 },
                    elevation: 2,
                  }}
                >
                  <View
                    className="w-8 h-8 rounded-lg items-center justify-center mb-2"
                    style={{ backgroundColor: colors.red.light }}
                  >
                    <XCircle size={16} color={colors.error} />
                  </View>
                  <Text style={{ color: colors.text.primary }} className="text-2xl font-bold" numberOfLines={1}>
                    {pipelineCounts.passed}
                  </Text>
                  <Text style={{ color: colors.text.tertiary }} className="text-xs" numberOfLines={1}>
                    Passed
                  </Text>
                </AnimatedPressable>
              </View>
            </Animated.View>

            {/* AI Recommended Deals */}
            {topDeals.length > 0 && (
              <Animated.View
                entering={FadeInUp.delay(300).duration(500)}
                className="px-5 mt-6"
              >
                <View className="flex-row items-center justify-between mb-3">
                  <View className="flex-row items-center gap-2">
                    <Sparkles size={18} color={colors.primary[600]} />
                    <Text style={{ color: colors.text.primary }} className="text-lg font-semibold">
                      Recommended for You
                    </Text>
                  </View>
                </View>

                <View className="gap-3">
                  {topDeals.map((deal, index) => (
                    <Animated.View
                      key={deal.id}
                      entering={FadeInRight.delay(350 + index * 100).duration(400)}
                    >
                      <AnimatedPressable
                        onPress={() => router.push(`/startup-detail?id=${deal.startup.id}`)}
                        className="rounded-2xl p-4"
                        style={{
                          backgroundColor: colors.surface.primary,
                          borderWidth: 1,
                          borderColor: colors.border.light,
                          shadowColor: '#000',
                          shadowOpacity: 0.05,
                          shadowRadius: 10,
                          shadowOffset: { width: 0, height: 2 },
                          elevation: 2,
                        }}
                      >
                        <View className="flex-row items-start justify-between">
                          <View className="flex-1">
                            <View className="flex-row items-center gap-2 mb-1">
                              <Text style={{ color: colors.text.primary }} className="font-semibold text-base">
                                {deal.startup.company.name}
                              </Text>
                              {deal.startup.funding.isActivelyRaising && (
                                <View
                                  className="px-2 py-0.5 rounded-full"
                                  style={{ backgroundColor: colors.traffic.greenLight }}
                                >
                                  <Text style={{ color: colors.success }} className="text-xs font-medium">
                                    Raising
                                  </Text>
                                </View>
                              )}
                            </View>
                            <Text
                              style={{ color: colors.text.secondary }}
                              className="text-sm"
                              numberOfLines={1}
                            >
                              {deal.startup.company.tagline}
                            </Text>
                            <View className="flex-row items-center gap-3 mt-2">
                              <Text style={{ color: colors.text.tertiary }} className="text-xs">
                                {deal.startup.company.industry}
                              </Text>
                              <Text style={{ color: colors.text.tertiary }} className="text-xs">
                                •
                              </Text>
                              <Text style={{ color: colors.text.tertiary }} className="text-xs">
                                {deal.startup.company.stage.replace('-', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                              </Text>
                              {(deal.startup.funding.currentRaise?.amount ?? 0) > 0 && (
                                <>
                                  <Text style={{ color: colors.text.tertiary }} className="text-xs">
                                    •
                                  </Text>
                                  <Text style={{ color: colors.text.tertiary }} className="text-xs">
                                    {mv(formatCurrency(deal.startup.funding.currentRaise!.amount))}
                                  </Text>
                                </>
                              )}
                            </View>
                          </View>

                          {/* Score Badge — canonical IRS band + venture label */}
                          {(() => {
                            const band =
                              deal.scoreEvent?.composite?.ventureLabel ??
                              (deal.readinessScore != null
                                ? scoreToLabel(deal.readinessScore)
                                : 'unscored');
                            const tint = labelColor(band);
                            return (
                              <View className="items-center ml-3">
                                <View
                                  className="w-14 h-14 rounded-2xl items-center justify-center"
                                  style={{ backgroundColor: `${tint}1A` }}
                                >
                                  <Text className="text-xl font-bold" style={{ color: tint }}>
                                    {deal.readinessScore ?? '—'}
                                  </Text>
                                </View>
                                <Text
                                  style={{ color: tint }}
                                  className="text-xs mt-1 font-semibold"
                                  numberOfLines={1}
                                >
                                  {capitalizeLabel(band)}
                                </Text>
                              </View>
                            );
                          })()}
                        </View>
                      </AnimatedPressable>
                    </Animated.View>
                  ))}
                </View>
              </Animated.View>
            )}
            </>
            ) : (
              <>
                <View className="px-5">
                  <SectionDivider label="Deal Pipeline" icon={Target} marginTop={24} marginBottom={12} />
                </View>
                <View className="px-5">
                  <PlatformDealflowLocked variant="section" />
                </View>
              </>
            )}

            {/* Bottom Spacer */}
            <View className="h-8" />
          </ScrollView>

          <AILauncherFAB targetRoute="/(investor)/assistant" flagKey="ai_assistant" />
        </ScreenFrame>
      </View>
    </AppLayout>
  );
}
